import React from 'react';
import { createContext, useState, useEffect, useContext } from "react";
import wretch from "wretch";
import { apiUrl } from "../api";
import honeybadger from "../honeybadger";

async function getSession() {
  const session = localStorage.getItem("professio-session");
  return session ? JSON.parse(session) : null;
}

async function setSession(session) {
  localStorage.setItem("professio-session", JSON.stringify(session));
}

async function removeSession() {
  localStorage.removeItem("professio-session");
}

const AuthContext = createContext();
const API_ROOT = import.meta.env.VITE_API_ROOT;
const APP_HOST = import.meta.env.VITE_APP_HOST;
let signOutInProgress = false;

export function isSignOutInProgress() {
  return signOutInProgress;
}

wretch()
  .errorType("json")
  .accept("application/json")
  .content("application/json");

const authClient = {
  signIn(form) {
    return wretch(`${API_ROOT}/api/auth/sign_in`)
      .post(form)
      .unauthorized((error) => {
        return error.json;
      })
      .res((response) => {
        if (response.ok) {
          return response.json().then(({ data }) => {
            const session = {
              "access-token": response.headers.get("access-token"),
              client: response.headers.get("client"),
              uid: response.headers.get("uid"),
            };
            return { success: true, data, session };
          });
        } else {
          return response.json().then((data) => {
            return { success: false, data };
          });
        }
      });
  },

  async signOut() {
    const headers = await getSession();
    return wretch(apiUrl("auth/sign_out"))
      .headers(headers)
      .delete()
      .notFound((error) => {
        return error.json;
      })
      .unauthorized((error) => {
        return error.json;
      })
      .json();
  },

  register(form) {
    return wretch(apiUrl("auth"))
      .post(form)
      .unauthorized((error) => {
        ////console.log("ERROR", error);
        return error.json;
      })
      .res((response) => {
        if (response.ok) {
          return response.json().then(({ data }) => {
            const session = {
              "access-token": response.headers.get("access-token"),
              client: response.headers.get("client"),
              uid: response.headers.get("uid"),
            };
            return { success: true, data, session };
          });
        } else {
          return response.json().then((data) => {
            return { success: false, data };
          });
        }
      });
  },
};

const validateSession = async (body) => {
  const paramsString = Object.entries(body)
    .reduce((params, [key, value]) => {
      return params.concat(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    }, [])
    .join("&");

  return wretch(apiUrl(`auth/validate_token?${paramsString}`))
    .get()
    .unauthorized(() => {
      return { success: false };
    })
    .json()
    .catch((error) => {
      honeybadger.notify(error);
      return { success: false };
    });
};

function AuthProvider(props) {
  const [user, setUser] = useState();
  const [initialLoadPerformed, setInitialLoadPerformed] = useState(false);
  useEffect(() => honeybadger.setContext({ user }), [user]);

  useEffect(() => {
    if (!initialLoadPerformed) {
      getSession().then((existingSession) => {
        if (existingSession) {
          validateSession(existingSession).then((response) => {
            ////console.log("existingSession", existingSession, response.data);

            if (response.success) {
              setUser({
                ...response.data,
                profile_picture_url: existingSession.profile_picture_url,
                user_interests: existingSession.user_interests,
              });
            }

            setInitialLoadPerformed(true);
          });
        } else {
          setInitialLoadPerformed(true);
        }
      });
    }
  }, [initialLoadPerformed]);

  const updateSession = () => {
    getSession().then((existingSession) => {
      if (existingSession) {
        ////console.log("ext");
      }
    });
  };

  const signIn = (form) =>
    authClient.signIn(form).then((response) => {
      if (response.success) {
        signOutInProgress = false;

        validateSession(response.session).then((valResponse) => {
          ////console.log("existingSession", existingSession, response.data);

          setUser({
            ...valResponse.data,
            profile_picture_url: response.data.profile_picture_url,
            user_interests: response.data.user_interests,
          });
        });

        const session = {
          ...response.session,
          profile_picture_url: response.data.profile_picture_url,
          user_interests: response.data.user_interests,
        };

        setSession(session);
      }

      return response;
    });

  const register = (form) =>
    authClient.register({ app_user: form }).then((response) => {
      if (response.success) {
        signOutInProgress = false;
        setSession(response.session);
        setUser(response.data);
      }

      return response;
    });

  const signOut = () =>
    {
      signOutInProgress = true;

      return authClient.signOut().catch(() => undefined).then(() => {
        removeSession();
        setUser(null);
      });
    };

  if (!initialLoadPerformed) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        register,
        setUser,
        updateSession,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

function useSession() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useUser must be used within a AuthProvider`);
  }
  return getSession();
}

function useUser() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useUser must be used within a AuthProvider`);
  }
  return context.user;
}

export { AuthProvider, useAuth, useUser, useSession, getSession, setSession };
